import { DISC_LETTER_NUMBER, SPECIAL_CONFIG_THRESHOLD, WARM_LETTER_NUMBER } from '../constants/apiConstants';
import { numberToLetters } from './letterNumber';

// Role helpers for the special (non-lettered) config groups. New code should
// use these instead of comparing letter_number against sentinel constants.
// Structural type so the helpers work with Config, form values, and API rows.
interface HasLetterNumber {
  letter_number?: number | null;
}

export function isWarmupConfig(config: HasLetterNumber | null | undefined): boolean {
  return config?.letter_number === WARM_LETTER_NUMBER;
}

export function isDiscardConfig(config: HasLetterNumber | null | undefined): boolean {
  return config?.letter_number === DISC_LETTER_NUMBER;
}

/** True for any special config group (warmup, discard, future additions). */
export function isSpecialConfig(config: HasLetterNumber | null | undefined): boolean {
  return isSpecialLetterNumber(config?.letter_number);
}

export function isSpecialLetterNumber(letterNumber: number | null | undefined): boolean {
  return letterNumber != null && letterNumber >= SPECIAL_CONFIG_THRESHOLD;
}

/** Display label for a special group; null for ordinary lettered configs. */
export function specialConfigLabel(config: HasLetterNumber | null | undefined): string | null {
  if (isWarmupConfig(config)) return 'Warmup';
  if (isDiscardConfig(config)) return 'Discards';
  return null;
}

/** Short label for tight columns (run lists, diagnostics). */
export function specialConfigShortLabel(config: HasLetterNumber | null | undefined): string | null {
  if (isWarmupConfig(config)) return 'WARM';
  if (isDiscardConfig(config)) return 'DISC';
  return null;
}

/**
 * Column/cell label for any config letter_number: the letter for ordinary
 * configs, the short role label (WARM/DISC) for special groups. Never throws
 * on out-of-range sentinels.
 */
export function configShortLabel(letterNumber: number | null | undefined): string {
  if (letterNumber == null) return '--';
  if (isSpecialLetterNumber(letterNumber)) {
    return specialConfigShortLabel({ letter_number: letterNumber }) ?? 'DISC';
  }
  return numberToLetters(letterNumber);
}

export function getWarmupConfig<T extends HasLetterNumber>(configs: T[] | null | undefined): T | undefined {
  return configs?.find((config) => isWarmupConfig(config));
}

export function getDiscardConfig<T extends HasLetterNumber>(configs: T[] | null | undefined): T | undefined {
  return configs?.find((config) => isDiscardConfig(config));
}
